import * as Sentry from '@sentry/browser';
import { User } from '@sr/types';
import React, { useState } from 'react';
import { ArrowRightIcon, Box, Button, Link, NModal, Text } from '../../ui';
import apiRequest from '../../utils/api-request';

const ProCancelModal = ({
  onCancel,
  user,
}: {
  onCancel: () => void;
  user: User;
}) => (
  <NModal title="Limited Time Offer" OpenButton={<OpenButton />}>
    <ModalContent user={user} onCancel={onCancel} />
  </NModal>
);

function ModalContent({
  user,
  closeModal,
  onCancel,
}: {
  user: User;
  closeModal?: () => void;
  onCancel: () => void;
}) {
  type CouponStatus = 'initial' | 'loading' | 'succeeded';
  const [cancelLoading, setCancelLoading] = useState(false);
  const [couponStatus, setCouponStatus] = useState<CouponStatus>('initial');

  function handleCancelClick() {
    setCancelLoading(true);

    apiRequest('stripe/subscription', { method: 'DELETE' })
      .then(() => {
        const surveyUrl = `https://standardresume.typeform.com/to/sThIJKAV#id=${
          user.id
        }&email=${encodeURIComponent(user.email || '')}`;
        closeModal && closeModal();
        onCancel();
        setCancelLoading(false);
        window.open(surveyUrl);
      })
      .catch((error) => {
        Sentry.captureException(error);
        alert(
          'An error occurred. Please try again or email hello@standardresume.co'
        );
      });
  }

  function handleDiscountClick() {
    setCouponStatus('loading');

    apiRequest('stripe/cancelation-coupon', { method: 'POST' })
      .then(() => {
        setCouponStatus('succeeded');
      })
      .catch((error) => {
        setCouponStatus('initial');
        Sentry.captureException(error);
        alert(
          'An error occurred. Please try again or email hello@standardresume.co'
        );
      });
  }

  if (couponStatus === 'succeeded') {
    return (
      <Box id="gi-cancel-pro-modal">
        <Text mb={6}>
          The 50% discount has been added. It will automatically be applied to
          all payments until you cancel.
        </Text>
      </Box>
    );
  }

  return (
    <Box id="gi-cancel-pro-modal">
      <Text mb={6}>
        Get <strong>50% off</strong> your subscription and keep working on your
        resume. The discount will automatically be applied to all payments until
        you cancel.
      </Text>
      <Button
        sx={{ justifyContent: 'space-between' }}
        mt={5}
        onClick={handleDiscountClick}
        disabled={couponStatus === 'loading'}
      >
        {couponStatus === 'loading' ? 'Loading...' : 'Get 50% off'}
        <ArrowRightIcon color="white" />
      </Button>
      <Button
        variant="secondary"
        mt={2}
        onClick={handleCancelClick}
        disabled={cancelLoading}
        data-testid="confirm-cancel"
      >
        {cancelLoading ? 'Canceling...' : 'No thanks, cancel subscription'}
      </Button>
    </Box>
  );
}

function OpenButton({ onClick }: { onClick?: () => void }) {
  return (
    <Link
      to=""
      onClick={(e: any) => {
        e.preventDefault();
        onClick && onClick();
      }}
      id="gi-cancel-pro-open"
      data-testid="start-cancel"
    >
      cancel it here
    </Link>
  );
}

export default ProCancelModal;
